import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi } from '../api/auth';

const AuthContext = createContext(null);

const STORAGE_KEY = 'inverow_auth';

// When the URL contains ?guest=1 (used by the admin "Landing preview" iframe),
// the auth provider returns a signed-out state regardless of stored tokens.
function isGuestPreview() {
  try { return new URLSearchParams(window.location.search).get('guest') === '1'; }
  catch { return false; }
}

function loadStored() {
  if (isGuestPreview()) return null;
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading]         = useState(true); // true until initial check done
  const refreshTimerRef               = useRef(null);

  // Schedule silent token refresh 5 min before expiry
  const scheduleRefresh = useCallback((expiresAt, refreshToken) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const msUntilExpiry = new Date(expiresAt) - Date.now();
    const delay = Math.max(msUntilExpiry - 5 * 60 * 1000, 10_000);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const data = await authApi.refresh(refreshToken);
        persist(data);
      } catch {
        clearAuth();
      }
    }, delay);
  }, []); // eslint-disable-line

  function persist(data) {
    // In guest preview, never write tokens back to localStorage — that would
    // hijack the parent window's session.
    if (!isGuestPreview()) localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setAccessToken(data.accessToken);
    setUser(data.user);
    scheduleRefresh(data.expiresAt, data.refreshToken);
  }

  function clearAuth() {
    if (!isGuestPreview()) localStorage.removeItem(STORAGE_KEY);
    setAccessToken(null);
    setUser(null);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  }

  // On mount – restore session from localStorage
  useEffect(() => {
    const stored = loadStored();
    if (stored?.accessToken && stored?.refreshToken) {
      // Try to verify token is still valid; if close to expiry, refresh immediately
      const msLeft = new Date(stored.expiresAt) - Date.now();
      if (msLeft > 60_000) {
        setAccessToken(stored.accessToken);
        setUser(stored.user);
        scheduleRefresh(stored.expiresAt, stored.refreshToken);
        setLoading(false);
      } else {
        // Token expired or about to expire — refresh now
        authApi.refresh(stored.refreshToken)
          .then(data => { persist(data); setLoading(false); })
          .catch(() => { clearAuth(); setLoading(false); });
      }
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    persist(data);
    return data;
  }, []); // eslint-disable-line

  const register = useCallback(async (email, password) => {
    const data = await authApi.register(email, password);
    persist(data);
    return data;
  }, []); // eslint-disable-line

  const logout = useCallback(async () => {
    try { if (accessToken) await authApi.logout(accessToken); } catch {}
    clearAuth();
  }, [accessToken]);

  const loginWithData = useCallback((data) => {
    persist(data);
  }, []); // eslint-disable-line

  return (
    <AuthContext.Provider value={{ user, accessToken, loading, login, register, logout, loginWithData }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
